import axios, { AxiosError } from 'axios';
import {
  AskResponse,
  AssistantValidationErrorDetail,
  HunterChatHistoryItem,
  HunterChatHistoryListResponse,
  HunterChatPagination,
  SaveHunterChatMessagePayload,
} from '../types/assistant.types';
import {
  ASSISTANT_HISTORY_PAGE_SIZE,
  limitAssistantCandidates,
} from '../utils/assistant.utils';
import axiosService from './axios.service';
import { getEndpointBase } from './api.service';

const assistantAxios = axios.create({
  baseURL: '/assistant-api',
  timeout: 120000,
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export class AssistantApiError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'AssistantApiError';
    this.statusCode = statusCode;
  }
}

const getValidationMessage = (error: AxiosError): string | undefined => {
  const detail = error.response?.data as
    | { detail?: AssistantValidationErrorDetail[] }
    | undefined;

  return detail?.detail?.[0]?.msg;
};

const normalizeHistoryItems = (
  data: HunterChatHistoryListResponse['data'],
): HunterChatHistoryItem[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray(data.data)) {
    return data.data;
  }

  return [];
};

const normalizeHistoryItem = (
  item: HunterChatHistoryItem,
): HunterChatHistoryItem => ({
  ...item,
  candidates: limitAssistantCandidates(item.candidates ?? []),
});

export const askAssistant = async (
  question: string,
  signal?: AbortSignal,
): Promise<AskResponse> => {
  try {
    const { data } = await assistantAxios.post<AskResponse>(
      '/ask',
      { question },
      { signal },
    );
    return {
      message: data.message ?? '',
      candidates: limitAssistantCandidates(data.candidates),
    };
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 422) {
        const validationMessage = getValidationMessage(axiosError);
        if (validationMessage) {
          console.warn('[assistant] validation error:', validationMessage);
        }
        throw new AssistantApiError('assistant.validationError', 422);
      }

      if (axiosError.code === 'ERR_NETWORK') {
        throw new AssistantApiError('assistant.error');
      }
    }

    throw new AssistantApiError('assistant.error');
  }
};

export const getHunterChatHistory = async (
  params?: { page?: number; perPage?: number },
  signal?: AbortSignal,
): Promise<{
  items: HunterChatHistoryItem[];
  pagination: HunterChatPagination;
}> => {
  const page = params?.page ?? 1;
  const perPage = params?.perPage ?? ASSISTANT_HISTORY_PAGE_SIZE;

  try {
    const { data } = await axiosService.get<HunterChatHistoryListResponse>(
      getEndpointBase('chat/history'),
      {
        params: { page, per_page: perPage },
        signal,
      },
    );

    const items = normalizeHistoryItems(data.data).map(normalizeHistoryItem);
    const pagination = data.pagination ?? {
      current_page: page,
      last_page: page,
      per_page: perPage,
      total: items.length,
    };

    return { items, pagination };
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    throw new AssistantApiError('assistant.error');
  }
};

export const saveHunterChatMessage = async (
  payload: SaveHunterChatMessagePayload,
): Promise<HunterChatHistoryItem> => {
  const { data } = await axiosService.post<{ data: HunterChatHistoryItem }>(
    getEndpointBase('chat'),
    {
      messageText: payload.messageText,
      aiResponse: payload.aiResponse,
    },
  );

  return normalizeHistoryItem(data.data);
};

export const deleteHunterChatHistory = async (
  historyId: string,
): Promise<void> => {
  await axiosService.delete(getEndpointBase(`chat/history/${historyId}`));
};

export const deleteAllHunterChatHistory = async (): Promise<void> => {
  await axiosService.delete(getEndpointBase('chat/history'));
};
